// ── usePolyUnrealizedPnl — the Polymarket twin of usePositionPnl's total ──
//
// Sums (live side-native mark − exact basis − fees) over the trader's open
// poly positions — the same formula as the Position Tracker's headline tile
// poly branch, so every surface shows the same number. Marks come from
// polyPositionLiveData (slug mid via the store's conditionId index);
// positions whose mark, fee schedule, or open-share fee allocation is unknown
// withhold the headline total and carry explicit coverage.
//
// Returns null until the positions endpoint has answered at least once.

import { useMemo } from 'react';
import { usePolymarketRelayPositions } from '@/hooks/usePolymarketRelayPositions';
import { usePolyFeeSchedules } from '@/hooks/usePolyFeeSchedules';
import { polyPositionLiveData } from '@/lib/polymarket/describeOrder';
import { useThrottledMarketVersion } from '@/hooks/ui/useThrottledMarketVersion';
import {
  computePolyUnrealizedCoverage,
  type UnrealizedCoverage,
} from '@/lib/accounting/unrealizedCoverage';

export function usePolyUnrealizedPnl(
  enabled: boolean = true,
): UnrealizedCoverage | null {
  const { positions, status } = usePolymarketRelayPositions({
    enabled,
    pollMs: 30_000,
    includePnl: false,
  });
  const version = useThrottledMarketVersion();
  const conditionIds = useMemo(() => positions.map((p) => p.ticker), [positions]);
  const { schedules } = usePolyFeeSchedules(conditionIds);

  return useMemo(() => {
    void version; // marks are imperative store reads — re-derive on ticks
    if (status !== 'ready') return null;
    return computePolyUnrealizedCoverage({
      positions,
      schedules,
      getNowCents: (position) =>
        polyPositionLiveData(position.ticker, position.side).nowCents,
    });
  }, [positions, status, version, schedules]);
}
